import type { RequestAutomation } from '@app/src/types/automations'

/**
 * Pure function to generate automation tooltip from pre-computed summary.
 * Use this at the parent level to avoid hook calls in every cell.
 *
 * @param automation - The automation object
 * @param summary - Pre-computed automation summary string
 * @param formatMessage - intl formatMessage function
 * @returns Tooltip string or undefined
 *
 * @example
 * // In parent scene:
 * const automationSummaries = useMemo(() => {
 *   const map = new Map()
 *   uniqueAutomations.forEach(automation => {
 *     const summary = useCompleteAutomationSummary(automation) // Called once per unique automation
 *     const tooltip = getAutomationTooltip(automation, summary, formatMessage)
 *     map.set(automation.id, tooltip)
 *   })
 *   return map
 * }, [uniqueAutomations, formatMessage])
 *
 * // In child cell:
 * <DateCell
 *   value={inquiry.createdAt}
 *   automationTooltip={automationSummaries.get(inquiry.requestAutomation?.id)}
 * />
 */
export function getAutomationTooltip(
  automation: RequestAutomation | undefined | null,
  summary: string,
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string,
): string | undefined {
  if (!automation) return undefined

  if (automation.deletedAt) {
    return formatMessage({ id: 'schemas.inquiry.triggeredByDeletedAutomation' })
  }

  return formatMessage(
    { id: 'schemas.inquiry.triggeredByActiveAutomation' },
    {
      isActive: automation.isActive ? 'true' : 'false',
      automationSummary: summary,
    },
  )
}
